import { useState, useEffect } from 'react';
import { useMounted } from '../components/useMounted';


export function useApiCall<TResponse, TArgs extends unknown[]>(apiCall: (...args: TArgs) => Promise<TResponse>) {
    const apiCallUseStateResult = useState<TResponse | null>(null);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isDataLoaded, setIsDataLoaded ] = useState(false);
    
    const isMounted = useMounted();

    const fetchData = async (...args: TArgs) => {
        // Set loading state to "true" immediately here to happen as synchronously as possible.
        setIsLoading(true);
        setIsDataLoaded(false);

        try {
            const result = await apiCall(...args);

            // This isMounted check is a simple protection
            // against memory leaks on component un-mounts.
            // This could be improved more by actually
            // canceling the in-progress call, but we can save that
            // optimization for later.
            if(isMounted) {
                apiCallUseStateResult[1](result);
            }
        }
        finally {
            if(isMounted) {
                setIsLoading(false);
                setIsDataLoaded(true);
            }
        }
    };

    return {
        apiCall: fetchData,
        data: apiCallUseStateResult[0],
        isLoading,
        isDataLoaded
    };
}

/**
 * Performs an API call at the beginning of a component's lifecycle.
 * Currently only works for endpoints that return lists.
 * @param apiCall A function that returns a promise of the data call.
 * @param deps Equivalent to the parameter of the same name on useState
 */
export const useInitialDataFromApiCall = <TResponse>(
    apiCall: () => Promise<TResponse>,
    deps: readonly any[]) => {

    const { 
        apiCall: fetchData, 
        data, 
        isLoading, 
        isDataLoaded, 
    } = useApiCall(apiCall);

    useEffect(() => {
        // Initialize asynchronous task.
        fetchData();    
    // If no deps are passed, make sure to enforce this only gets called once (with empty array).
    // Note: ESLint is freaking out about the below. I don't feel comfortable enough yet to silence the warnings,
    // so I will let them stay until my knowledge grows.
    }, deps);

    return {
        data,
        isLoading,
        isDataLoaded,
        reFetchData: fetchData
    };
};